import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useMediaQuery } from "hooks/utils/useMediaQuery";
import { useMorningAuthStore } from "store/useMorningAuthStore";
import logo_air from "../assets/logo/logo_air.png";
import logo_morning from "../assets/logo/logo_morning.png";
import logo_quick from "../assets/logo/logo_quick.png";
import logo_express from "../assets/logo/logo_express.png";
import CompanyIntro from "assets/banner/company_intro.png";
import { ReactComponent as Close } from "assets/morning/icon/close.svg";
import { useAirAuthStore } from "../store/useAirAuthStore";
import { ilshinGroupPhone, siteMap } from "hooks/utils/util";
import { useMasterAuthStore } from "../store/useMasterAuthStore";
import { useQuickAuthStore } from "../store/useQuickAuthStore";
import { useExpressAuthStore } from "../store/useExpressAuthStore";
import { Sheet, SheetClose, SheetContent, SheetDescription, SheetFooter, SheetHeader, SheetTitle, SheetTrigger } from "./ui/sheet";

interface NavItem {
    label: string;
    path: string;
}

interface NavBarProps {
    transport: NavItem[];
    customer: NavItem[];
    support: NavItem[];
}

const NavBar = ({ transport, customer, support }: NavBarProps) => {
    const navigate = useNavigate();

    const responsive1200 = useMediaQuery(1200);
    const subdomain = window.location.hostname.split(".")[0];
    const [hoveredMenu, setHoveredMenu] = useState<string | null>(null);
    const [type, setType] = useState<"air" | "morning" | "quick" | "express">("air");
    const [openSiteMap, setOpenSiteMap] = useState(false);

    const accessTokenMorning = useMorningAuthStore(state => state.accessToken);
    const accessTokenAir = useAirAuthStore(state => state.accessToken);
    const accessTokenQuick = useQuickAuthStore(state => state.accessToken);
    const accessTokenExpress = useExpressAuthStore(state => state.accessToken);
    const masterIsGuest = useMasterAuthStore(state => state.isGuest ?? true);
    const morningCCompany = useMorningAuthStore(state => state.c_company);
    const masterCCompany = useMasterAuthStore(state => state.c_company);
    const isMasterPage = window.location.pathname.includes("/master");
    const isLoggedIn = isMasterPage
        ? !masterIsGuest
        : subdomain === "morning"
          ? !!accessTokenMorning
          : subdomain === "air"
            ? !!accessTokenAir
            : subdomain === "quick"
              ? !!accessTokenQuick
              : subdomain === "express"
                ? !!accessTokenExpress
                : false;
    const userCompany = isMasterPage ? masterCCompany : morningCCompany;

    const handleLogout = () => {
        if (subdomain === "morning") {
            useMorningAuthStore.getState().clearAuth();
        } else if (subdomain === "air") {
            useAirAuthStore.getState().clearAuth();
        } else if (subdomain === "quick") {
            useQuickAuthStore.getState().clearAuth();
        } else if (subdomain === "express") {
            useExpressAuthStore.getState().clearAuth();
        } else {
            console.warn("Unknown subdomain, no logout performed.");
        }

        navigate("/");
    };
    const logo = type === "air" ? logo_air : type === "morning" ? logo_morning : type === "quick" ? logo_quick : logo_express;

    const menuItems = ["회사소개", "운송 서비스", "고객 서비스", "고객센터"];
    const companyItems: NavItem[] = [
        { label: "회사소개 / CEO인사말", path: "/company/ceo-message" },
        { label: "연혁", path: "/company/history" },
        { label: "조직도", path: "/company/organization" },
        { label: "CI소개", path: "/company/ci" },
        { label: "BRANCH", path: "/company/branch" },
        { label: "오시는길", path: "/company/location" },
    ];

    if (type === "morning") {
        menuItems.push("채용지원");
    }

    const getMenuItems = () => {
        switch (hoveredMenu) {
            case "회사소개":
                return companyItems;
            case "운송 서비스":
                return transport;
            case "고객 서비스":
                return customer;
            case "고객센터":
                return support;
            case "채용지원":
                return [{ label: "채용지원", path: "/recruit" }];
            default:
                return [];
        }
    };

    const getMenuItemsForLabel = (label: string): NavItem[] => {
        switch (label) {
            case "회사소개":
                return companyItems;
            case "운송 서비스":
                return transport;
            case "고객 서비스":
                return customer;
            case "고객센터":
                return support;
            case "채용지원":
                return [{ label: "채용지원", path: "/recruit" }];
            default:
                return [];
        }
    };

    const getEnglishTitle = () => {
        switch (hoveredMenu) {
            case "회사소개":
                return "C O M P A N Y";
            case "운송 서비스":
                return "T R A N S P O R T";
            case "고객 서비스":
                return "C U S T O M E R";
            case "고객센터":
                return "C E N T E R";
            default:
                return "R E C R U I T M E N T N O T I C E";
        }
    };

    useEffect(() => {
        const subdomain = window.location.hostname.split(".")[0];
        if (["air", "morning", "quick", "express"].includes(subdomain)) {
            setType(subdomain as "air" | "morning" | "quick" | "express");
        } else {
            setType("air");
        }
    }, []);

    useEffect(() => {
        if (responsive1200 && openSiteMap) {
            setOpenSiteMap(false);
        }
    }, [responsive1200]);

    const tagetSitemap = siteMap.find(item => item.type === type);

    const titleMap = {
        air: "일신항공해운",
        express: "일신특송화물",
        morning: "일신모닝택배",
        quick: "일신모닝퀵",
    };

    return (
        <div className="w-full flex flex-col items-center pt-10  border-b">
            <div className="w-full max-w-[1200px] h-[75px] flex items-center justify-between responsive1200:px-[15px]">
                <h1>
                    <Link to="/">
                        <span className="sr-only">{titleMap[type]} - 국내외 물류 전문 일신그룹</span>
                        <img
                            src={logo}
                            alt={titleMap[type]}
                            className={`cursor-pointer animate-fade-in ${type === "quick" ? "mr-[23px]" : ""}`}
                            width={type === "quick" ? "197px" : "220px"}
                        />
                    </Link>
                </h1>

                {/* 1200 이하/이상 */}
                {responsive1200 ? (
                    <Sheet>
                        <SheetTrigger asChild>
                            <button
                                type="button"
                                title="사이드 메뉴 열기"
                                className="flex flex-col items-end justify-center gap-[5px] py-[5px]"
                            >
                                <span className={`w-[28px] h-[3px] bg-${type}`}></span>
                                <span className="w-[20px] h-[3px] bg-[#0e0057]"></span>
                                <span className="w-[20px] h-[3px] bg-[#0e0057]"></span>
                            </button>
                        </SheetTrigger>
                        <SheetContent className="bg-white p-0 overflow-auto w-full max-w-[400px]">
                            <SheetHeader>
                                <SheetTitle hidden />
                                <SheetDescription hidden />
                            </SheetHeader>

                            <div className="flex flex-col">
                                <div className="hidden responsive1200:flex p-4 bg-white sticky top-0 justify-between z-[2] border-b">
                                    <h2 className="h-[55px] flex items-center">
                                        <a
                                            href="/"
                                            style={{
                                                backgroundImage: `url(${logo})`,
                                                width: type === "quick" ? "162px" : "180px",
                                                height: type === "morning" ? "36px" : "33px",
                                            }}
                                            className="bg-no-repeat bg-cover inline-block indent-[-9999em]"
                                        >
                                            일신{type}
                                        </a>
                                    </h2>
                                    <SheetClose asChild className="cursor-pointer">
                                        <Close />
                                    </SheetClose>
                                </div>

                                <div className="flex-1 flex flex-col overflow-auto">
                                    <div>
                                        {menuItems.map((item, index) => {
                                            const isOpen = hoveredMenu === item;
                                            return (
                                                <div key={index}>
                                                    <div
                                                        onClick={() => setHoveredMenu(prev => (prev === item ? null : item))}
                                                        className="cursor-pointer p-[15px_20px] border-b flex justify-between"
                                                    >
                                                        <span>{item}</span>
                                                        <div className="flex items-center relative">
                                                            <span className={`w-[16px] h-[2px] bg-${type}`}></span>
                                                            <span
                                                                className={`w-[2px] h-[16px] bg-${type} absolute top-1/2 left-1/2 -translate-y-1/2 -translate-x-1/2`}
                                                            ></span>
                                                        </div>
                                                    </div>

                                                    {isOpen && (
                                                        <div
                                                            className={`overflow-hidden transition-all duration-300 delay-[0.5s] bg-[#edf5fa] py-[10px] flex flex-col ${isOpen ? "max-h-[auto]" : "max-h-0"}`}
                                                        >
                                                            {getMenuItems().map((subItem, subIndex) => (
                                                                <Link
                                                                    to={subItem.path}
                                                                    key={subIndex}
                                                                    className="pl-4 text-[#0e0057] text-[14px] p-[6px_20px]"
                                                                >
                                                                    {subItem.label}
                                                                </Link>
                                                            ))}
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>

                                    <div className="mx-4 my-5 flex flex-col gap-[10px]">
                                        {subdomain === "morning" && isLoggedIn && (
                                            <div className="whitespace-pre-line text-[#666] py-2">
                                                <span className="text-new font-semibold">{userCompany}</span> 님
                                            </div>
                                        )}

                                        <div className="flex gap-[10px] items-center">
                                            {isLoggedIn ? (
                                                <>
                                                    <button
                                                        type="button"
                                                        className="flex-1 text-center rounded-[22px] bg-[#20b6e5] p-[8px_15px] text-white text-[15px] font-[500]"
                                                        onClick={handleLogout}
                                                    >
                                                        LOGOUT
                                                    </button>
                                                    {subdomain === "morning" && (
                                                        <Link
                                                            to="/mypage"
                                                            className="flex-1 text-center rounded-[22px] bg-[#20b6e5] p-[8px_15px] text-white text-[15px] font-[500]"
                                                        >
                                                            마이페이지
                                                        </Link>
                                                    )}
                                                </>
                                            ) : (
                                                <>
                                                    <Link
                                                        to="/login"
                                                        className="flex-1 text-center rounded-[22px] bg-[#20b6e5] p-[8px_15px] text-white text-[15px] font-[500]"
                                                    >
                                                        LOGIN
                                                    </Link>
                                                    <Link
                                                        to="/signup"
                                                        className="flex-1 text-center rounded-[22px] bg-[#20b6e5] p-[8px_15px] text-white text-[15px] font-[500]"
                                                    >
                                                        JOIN
                                                    </Link>
                                                </>
                                            )}
                                        </div>

                                        <a
                                            href={`tel:${ilshinGroupPhone(type)}`}
                                            className="flex-1 rounded-[22px] bg-[#0e0057] p-[8px_15px] text-white text-[15px] font-[500] text-center"
                                        >
                                            전화하기
                                            {/* <img src="" alt="" /> */}
                                        </a>
                                    </div>

                                    <div className="mx-4 mb-4 border rounded-[10px] border-[#ddd] relative p-[28px_20px] overflow-hidden">
                                        <p className="text-[18px] pb-[10px] text-[#333333] font-[500]">고객센터</p>
                                        <div className="text-[15px] text-[#333333]">
                                            <p>평일 : 09:00~18:00</p>
                                            <p>토요일 : 09:00~12:00</p>
                                        </div>
                                        <p className={`text-[20px] font-bold text-${type}`}>{ilshinGroupPhone(type)}</p>
                                        <img src={CompanyIntro} alt="" className="absolute right-0 top-0 -translate-y-1/3" />
                                    </div>
                                </div>
                            </div>
                        </SheetContent>
                    </Sheet>
                ) : (
                    <>
                        <div className="responsive1200:hidden flex flex-1 items-center">
                            <div
                                className={`${type === "morning" ? "" : "pl-[38px]"} items-center h-[75px] mx-10 w-full flex justify-between text-[#333] font-[500] text-[19px]`}
                            >
                                {menuItems.map((item, index) => (
                                    <span
                                        className={`justify-center h-[75px] flex items-center w-full cursor-pointer z-10 relative after:content-[''] after:absolute after:h-[2px] after:w-full ${
                                            type === "air"
                                                ? "after:bg-air"
                                                : type === "morning"
                                                  ? "after:bg-morning"
                                                  : type === "quick"
                                                    ? "after:bg-quick"
                                                    : "after:bg-express"
                                        } after:bottom-0 after:left-0 after:transition-transform after:duration-50 ${hoveredMenu === item ? `text-${type} after:scale-x-[80%]` : "after:scale-x-0"}`}
                                        key={index}
                                        onMouseEnter={() => !openSiteMap && setHoveredMenu(item)}
                                        onMouseLeave={() => !openSiteMap && setHoveredMenu(null)}
                                        onClick={() => {
                                            const items = getMenuItemsForLabel(item);
                                            if (items.length > 0) navigate(items[0].path);
                                        }}
                                    >
                                        {item}
                                    </span>
                                ))}
                            </div>
                            <div className={`text-xs gap-[14px] relative flex ${type === "morning" ? "" : ""}`}>
                                {isLoggedIn ? (
                                    <>
                                        <p onClick={handleLogout} className="text-[#666] cursor-pointer">
                                            LOGOUT
                                        </p>
                                        {subdomain === "morning" && (
                                            <p onClick={() => navigate("/mypage")} className="text-[#666] cursor-pointer">
                                                MYPAGE
                                            </p>
                                        )}
                                        {(subdomain === "quick" || subdomain === "express") && (
                                            <p onClick={() => navigate("/mypage")} className="text-[#666] cursor-pointer">
                                                EDIT
                                            </p>
                                        )}
                                    </>
                                ) : (
                                    <>
                                        <p onClick={() => navigate("/login")} className="text-[#666] cursor-pointer">
                                            LOGIN
                                        </p>
                                        <p onClick={() => navigate("/signup")} className="text-[#666] cursor-pointer">
                                            JOIN
                                        </p>
                                    </>
                                )}

                                <div className="flex items-center gap-2 cursor-pointer" onClick={() => setOpenSiteMap(pre => !pre)}>
                                    <button
                                        type="button"
                                        className={`bg-none border-none ${
                                            type === "air"
                                                ? "text-air"
                                                : type === "morning"
                                                  ? "text-morning"
                                                  : type === "quick"
                                                    ? "text-quick"
                                                    : "text-express"
                                        }`}
                                    >
                                        SITEMAP
                                    </button>
                                    <div className="flex flex-col gap-[3px] mb-0.5">
                                        <div
                                            className={`${
                                                type === "air"
                                                    ? "bg-air"
                                                    : type === "morning"
                                                      ? "bg-morning"
                                                      : type === "quick"
                                                        ? "bg-quick"
                                                        : "bg-express"
                                            } w-3 h-[1px]`}
                                        />
                                        <div
                                            className={`${
                                                type === "air"
                                                    ? "bg-air"
                                                    : type === "morning"
                                                      ? "bg-morning"
                                                      : type === "quick"
                                                        ? "bg-quick"
                                                        : "bg-express"
                                            } w-3 h-[1px]`}
                                        />
                                        <div
                                            className={`${
                                                type === "air"
                                                    ? "bg-air"
                                                    : type === "morning"
                                                      ? "bg-morning"
                                                      : type === "quick"
                                                        ? "bg-quick"
                                                        : "bg-express"
                                            } w-3 h-[1px]`}
                                        />
                                    </div>
                                </div>
                                {subdomain === "morning" && isLoggedIn && (
                                    <div className="absolute right-0 top-6 text-[15px] whitespace-nowrap text-[#666]">
                                        <span className="text-new font-semibold">{userCompany}</span> 님 환영합니다
                                    </div>
                                )}
                            </div>
                        </div>
                    </>
                )}
            </div>
            {!responsive1200 && hoveredMenu && (
                <div
                    onMouseEnter={() => setHoveredMenu(hoveredMenu)}
                    onMouseLeave={() => setHoveredMenu(null)}
                    className="h-[150px] bg-white z-10 w-full border-y items-center flex justify-center"
                >
                    <div className="w-[1200px] flex items-center relative">
                        <div className="flex flex-col gap-2 mt-3">
                            <p className="text-[#878A93] text-sm">{getEnglishTitle()}</p>
                            <p className="text-3xl font-[500] text-[#333]">{hoveredMenu}</p>
                        </div>
                        <div
                            className={`grid grid-cols-3 gap-3.5 w-[750px] absolute ${type === "morning" ? "left-[280px]" : "left-[330px]"}`}
                        >
                            {getMenuItems().map(({ label, path }, index) => (
                                <div
                                    key={index}
                                    className="flex min-h-3.5 items-center group cursor-pointer relative"
                                    onClick={() => navigate(path)}
                                >
                                    <div className="h-3.5 w-[2px] bg-gray-300 transition-all duration-300 ease-in-out group-hover:h-[2px] group-hover:w-3.5 group-hover:bg-[#0e0057] origin-center transform group-hover:-translate-x-[5px]" />
                                    <span className="absolute left-7 text-[#666] text-[14px] font-[400] group-hover:text-[#0e0057]">
                                        {label}
                                    </span>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}
            {!responsive1200 && openSiteMap && (
                <div className="bg-white z-10 w-full border-y items-center flex justify-center">
                    <div className={`w-full max-w-[1200px] flex responsive1300:px-[2%]`}>
                        {tagetSitemap?.depth1.map(menu => {
                            return (
                                <div key={menu.menu} className="flex-1 hover:bg-[#f8f8f8] py-[38px] px-[1%]">
                                    <p className="text-[#333333] font-[500] leading-[33px] text-[23px] flex gap-[15px] items-center cursor-default">
                                        <span className={`w-[3px] h-[20px] bg-${type}`}></span>
                                        {menu.menu}
                                    </p>
                                    <div className="flex flex-col mt-[20px] pl-[17px]">
                                        {menu.subMenu.map(subMenu => {
                                            return (
                                                <Link to={subMenu.path} key={subMenu.menu} className="text-[15px] text-[#666666] py-[5px]">
                                                    {subMenu.menu}
                                                </Link>
                                            );
                                        })}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}
        </div>
    );
};

export default NavBar;
